import { Component, ElementRef, ViewChild, OnDestroy, AfterViewInit, Pipe, ChangeDetectorRef } from '@angular/core';
import { IBluePrintModel } from '../../../../../models/blueprint/blueprint.model';
import { PatchBluePrintRootPayload } from '../../../../../models/blueprint/patch-blue-print-root-payload.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Helper } from '../../../../../services/helper.service';
import { DashboardHelper } from '../../../../../services/dashboard.service';
import { UiStateStore } from '../../../../../services/stores/ui-state.store';
import { BluePrintAPIService } from '../../../../../services/api/blueprint.api.service';
import { BlueprintStore } from '../../../../../services/stores/blueprint.store';
import { BaseComponent } from '../../../../../components/base/base.component';
import { UserService } from '../../../../../services/user.service';
import { PermissionService } from '../../../../../services/permission.service';

@Component({
  selector: 'app-blueprint-root-property',
  templateUrl: './blueprint-root-property.component.html',
  styleUrls: ['./blueprint-root-property.component.css'],
  standalone: false,
})
export class BlueprintRootPropertyComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  selectedBluePrint: IBluePrintModel = {} as IBluePrintModel;
  selectedBluePrintOldVal: IBluePrintModel = {} as IBluePrintModel;
  oldName: string = '';
  @ViewChild('inputName') inputNameElement!: ElementRef;
  myForm: FormGroup;

  constructor(
    private dashboardHelper: DashboardHelper,
    private bluePrintService: BluePrintAPIService,
    private bluePrintStore: BlueprintStore,
    private helperService: Helper,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private uiStateStore: UiStateStore,
    private userService: UserService,
    private permissionService: PermissionService,
  ) {
    super();
    this.myForm = this.fb.group({
      name: ['', Validators.required],
      description: '',
    });
    // Reset the form value if panel is hidden
    this.subscribers.rightPanel = this.dashboardHelper.$showRightPanel.subscribe((show) => {
      if (this.selectedBluePrint) {
        this.myForm.controls.name.setValue(this.selectedBluePrint.name);
        this.myForm.controls.description.setValue(this.selectedBluePrint.description);
      }
    });
  }

  public get isReadOnly(): boolean {
    if (this.permissionService.isSysAdmin) {
      return false;
    }
    const blueprintOrgId = this.selectedBluePrint?.organizationId?.toLowerCase();
    const userOrgId = this.userService.user?.organizationId?.toLowerCase();
    if (!blueprintOrgId || !userOrgId) {
      // Unknown org on either side - fail closed (read-only) rather than treat two empty/missing
      // org ids as a match.
      return true;
    }
    return blueprintOrgId !== userOrgId;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.subscribers.rightPanelVisible = this.dashboardHelper.$showRightPanel.subscribe((panelVisibility) => {
        if (panelVisibility === false) {
          // If right side panel is closed without saving then reset to previous value
          if (this.selectedBluePrint && this.selectedBluePrintOldVal) {
            this.selectedBluePrint = this.selectedBluePrintOldVal;
            if (this.myForm && this.myForm.dirty && this.myForm.valid) {
              this.myForm.markAsPristine();
              this.cdr.detectChanges();
            }
          }
        }
      });

      // this.subscribers.selectedItemChanged = this.dashboardHelper.selectedItem.subscribe((result) => {
      //   if (this.selectedBluePrint && this.selectedBluePrint.id !== result) {
      //     if (this.myForm && this.myForm.dirty && this.myForm.valid) {
      //       this.myForm.markAsPristine();
      //       this.saveWork();
      //     }
      //   }
      // });

      this.subscribers.bp = this.bluePrintStore.$selectedBluePrint.subscribe((bp) => {
        if (bp) {
          this.selectedBluePrint = bp;
          this.dashboardHelper.setSharedRightPanelHeaderTitle('Template Root');
          this.selectedBluePrintOldVal = this.helperService.getDeepCopy(bp);
          this.myForm.controls.name.setValue(this.selectedBluePrint.name);
          this.myForm.controls.description.setValue(this.selectedBluePrint.description);
          if (this.isReadOnly) {
            this.myForm.disable();
          } else {
            this.myForm.enable();
          }
          this.oldName = this.selectedBluePrint.name;
          this.dashboardHelper.setShowRightPanel(true);
          this.cdr.detectChanges();
        }
      });
    });
  }

  public selectName() {
    const value = this.inputNameElement?.nativeElement?.value?.toLowerCase() ?? '';
    if (value.startsWith('blueprint')) {
      this.inputNameElement?.nativeElement?.select();
    }
  }

  public saveWork() {
    if (this.isReadOnly) {
      return;
    }
    if (!this.selectedBluePrint || !this.selectedBluePrintOldVal) {
      return;
    }

    const originalName = this.selectedBluePrintOldVal.name;
    const originalDescription = this.selectedBluePrintOldVal.description;

    // Get values from form but DON'T modify selectedBluePrint yet
    const newName = this.myForm?.value?.name?.trim();
    const newDescription = this.myForm?.value?.description?.trim() || '';

    const patchBluePrintRootPayload = new PatchBluePrintRootPayload(
      this.selectedBluePrint.id,
      newName, // Use form values directly
      newDescription, // Use form values directly
      '',
      '',
      '',
      '',
      this.selectedBluePrint.isArchived,
      this.selectedBluePrint.parent,
    );

    console.log('saving template...' + this.selectedBluePrint.id);
    this.uiStateStore.startBackendAction('Saving BluePrint...');

    this.subscribers.patchRoot = this.bluePrintStore.patchRoot(patchBluePrintRootPayload).subscribe({
      next: (result) => {
        console.log(result);
        if (result == true) {
          // Only update after success
          this.selectedBluePrint.name = newName;
          this.selectedBluePrint.description = newDescription;
          this.selectedBluePrintOldVal = this.helperService.getDeepCopy(this.selectedBluePrint);
          this.oldName = newName;

          this.cdr.detectChanges();
          this.dashboardHelper.setModifiedPropertyItem({
            id: this.selectedBluePrint.id,
            name: newName,
          });
          this.uiStateStore.endBackendAction('BluePrint Saved!');
        }
      },
      error: (error) => {
        this.uiStateStore.endBackendAction('Error saving BluePrint!');
        console.error('Error saving template:', error);

        // Since we never modified selectedBluePrint, just reset the form
        this.myForm.controls.name.setValue(originalName);
        this.myForm.controls.description.setValue(originalDescription);
        this.myForm.markAsPristine();

        this.cdr.detectChanges();

        // Show error message to user
        let errorMessage = 'Failed to save blueprint.';
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error?.error === 'string') {
          errorMessage = error.error;
        }

        alert(errorMessage);
      },
    });
  }

  public onNameBlur() {
    if (this.isReadOnly) {
      return;
    }
    if (this.selectedBluePrint.name === '') {
      this.selectedBluePrint.name = this.oldName; // revert to old name
      this.dashboardHelper.setModifiedPropertyItem({
        // update the diagram
        id: this.selectedBluePrint.id,
        name: this.selectedBluePrint.name,
      });
    } else {
      this.oldName = this.selectedBluePrint.name;
    }
  }

  public onChange() {
    if (this.isReadOnly) {
      return;
    }
    if (this.selectedBluePrint.name === '') {
      return;
    }
    // this.bluePrintService.setSelectedBluePrint(this.selectedBluePrint); // update the modified model back to session
    this.dashboardHelper.setModifiedPropertyItem({
      id: this.selectedBluePrint.id,
      name: this.selectedBluePrint.name,
    });
  }
}
